import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { isFulfilled } from '@reduxjs/toolkit';

import AppRoutes from 'routes/AppRouting';
import { setCampaignAnalytics } from 'store/analytics';
import { getCustomer } from 'store/customer/customer.thunk';
import { auth0Config, authService } from 'store/customer/utils/auth0.util';

import { config } from '../config/env-config';
import { screenBreakpoints } from '../config/track-config';
import { getDeliveryPreferences } from '../store/deliveryPreference';
import { useAppDispatch } from '../store/store';
import { messageExchangeConfig, readMxMessage } from '../utils/message-exchange.utils';
import {
  attachNewRelicErrorSuppressions,
  CACHE_SEARCHED_ARTICLES,
  deleteCacheItem,
  reportNewRelicError,
} from '../utils/window.utils';
import Footer from './common/Footer/Footer';
import Header from './common/Header/Header';
import Breadcrumb from './DetailsPage/common/Breadcrumb/Breadcrumb';
import { APP_TEST_ID } from './App.test-ids';

import styles from './App.module.scss';

// Attach global error suppression for fullstop chatbot 403s
attachNewRelicErrorSuppressions();

const App = () => {
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const guidToken = searchParams.get('token');
  const ilink = searchParams.get('ilink') ?? '';
  const iedm = searchParams.get('iedm') ?? '';
  const [isInitialised, setIsInitialised] = React.useState(false);

  React.useEffect(() => {
    // init auth0
    if (!authService) return;
    authService
      .initialise(auth0Config)
      .then(() => setIsInitialised(true))
      .catch((err) => reportNewRelicError(err));

    // init branch
    if (window.innerWidth < screenBreakpoints.md && window.branch?.init) {
      window.branch.init(config.api.branchIo, {}, (err: unknown) => {
        if (err) reportNewRelicError(err);
      });
    }
  }, []);

  React.useEffect(() => {
    // listen for guid token search param
    isInitialised &&
      dispatch(getCustomer(guidToken))
        .then((getCustomerAction) => {
          if (!isFulfilled(getCustomer)(getCustomerAction)) return;
          const { isAuthenticated, knownToken } = getCustomerAction.payload;
          if (isAuthenticated || knownToken) dispatch(getDeliveryPreferences());
          if (isAuthenticated) deleteCacheItem(CACHE_SEARCHED_ARTICLES);
        })
        .catch((err) => reportNewRelicError(err));
  }, [guidToken, isInitialised]);

  React.useEffect(() => {
    // listen for campaign data - ilink & iedm search params
    dispatch(setCampaignAnalytics({ ilink, iedm }));
  }, [ilink, iedm]);

  React.useEffect(() => {
    // check for redirect from ULP mx msg
    const hasRedirectFromLoginMsg = readMxMessage(messageExchangeConfig.redirectFromLogin);
    let newUrl = '/search';
    if (hasRedirectFromLoginMsg && hasRedirectFromLoginMsg.length > 0) {
      newUrl = hasRedirectFromLoginMsg[0].payload.redirectUrl;
      return window.location.assign(newUrl);
    }
  }, []); // runs once

  return (
    <div className={styles.app} data-testid={APP_TEST_ID}>
      {iedm && <meta name='branch:deeplink:iedm' content={iedm} />}
      <Header />
      <Breadcrumb />
      <AppRoutes />
      <Footer />
    </div>
  );
};

export default App;
